/**
 * Cliente tRPC del POS web.
 *
 * Habla HTTP contra `/trpc` del worker. Auth:
 *   - Cada request adjunta `Authorization: Bearer <accessToken>`.
 *   - Si el access token expira en <30s, se refresca PROACTIVAMENTE
 *     antes de la llamada — evita el roundtrip 401 → refresh → retry.
 *   - Si aun así responde 401, hacemos un refresh + retry (una vez).
 *   - Si el refresh falla (token revocado/expirado/red caída), limpiamos
 *     sesión y recargamos para mostrar el login.
 *
 * Tipo del router importado SOLO como type (`import type`), se elimina
 * en build — no trae código del worker al bundle del browser.
 */

import {
  createTRPCReact,
  httpBatchLink,
  type CreateTRPCReact,
} from '@trpc/react-query';

import { loadSession } from '../auth/session.js';

import type { AppRouter } from '@atreus/worker/src/trpc/router.ts';
import { handleAuthFailure, refresh, WORKER_URL } from './cloud.js';

export const trpc: CreateTRPCReact<AppRouter, unknown> =
  createTRPCReact<AppRouter>();

/**
 * Fetch wrapper con refresh transparente:
 *   1. Si hay sesión y el access está por expirar (<30s), refresca antes.
 *   2. Agrega Authorization header.
 *   3. Si el response es 401 con una sesión activa, intenta refresh + retry
 *      una única vez.
 *   4. Si el refresh en cualquier paso falla, limpia sesión y recarga.
 */
// Tipamos flojo porque `@cloudflare/workers-types` (agregado para poder
// importar el type `AppRouter`) extiende el global `fetch` con campos CF
// que tRPC no conoce. El runtime usa el `fetch` del browser sin problema.
const authenticatedFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  let session = loadSession();

  if (session && session.accessExpiresAt < Date.now() + 30_000) {
    try {
      session = await refresh(session);
    } catch {
      handleAuthFailure();
      // Seguimos sin session; el request probablemente fallará con 401
      // pero handleAuthFailure ya recargó — el usuario ve el login.
      session = null;
    }
  }

  const headers = new Headers(init?.headers);
  if (session) headers.set('authorization', `Bearer ${session.accessToken}`);

  const first = await fetch(input, { ...init, headers });
  if (first.status !== 401 || !session) return first;

  // 401 con sesión → refresh + retry.
  try {
    session = await refresh(session);
  } catch {
    handleAuthFailure();
    return first;
  }
  const retryHeaders = new Headers(init?.headers);
  retryHeaders.set('authorization', `Bearer ${session.accessToken}`);
  return fetch(input, { ...init, headers: retryHeaders });
};

export const trpcClientLink = httpBatchLink({
  url: `${WORKER_URL}/trpc`,
  // `@cloudflare/workers-types` (agregado al tsconfig para poder importar
  // el tipo `AppRouter` del worker) redeclara `RequestInit` con un shape
  // levemente distinto (`signal: AbortSignal | null` vs tRPC's
  // `AbortSignal | undefined`). Runtime son compatibles; cast aislado.
  fetch: authenticatedFetch as never,
});
